import { createSearchParams } from 'react-router';

import { Button, Menu, MenuButton, MenuDivider, MenuList, MenuOptionGroup, Portal } from '@chakra-ui/react';

import { CheckDouble, ChevronDown, HasAccess, HouseUser, Pickaxe } from '@enterprise/ui';

import type { MailingTypeMenuProps } from './types/mailing-type-menu.props';

import { useMailingTypeMenuConfiguration } from './hooks/mailing-type-menu-configuration.hook';
import RadioMenuItem from './radio-menu-item';

const MailingTypeMenu = ({
  bottomMenuItemsSlot,
  children,
  externalMenuItemsSlot,
  hspMenuItemsSlot,
  isActive,
  label,
  pathSegment,
}: MailingTypeMenuProps) => {
  const { isOpen, onClose, onOpen, selectedMenu } = useMailingTypeMenuConfiguration();

  return (
    <Menu
      gutter={0}
      isLazy
      isOpen={isOpen}
      matchWidth>
      <MenuButton
        _expanded={
          isActive ? { bg: 'hsp.500', color: 'white' } : { boxShadow: '0 0 0 3px var(--chakra-colors-hsp-200)' }
        }
        _focus={{ boxShadow: '0 0 0 3px var(--chakra-colors-hsp-200)' }}
        _hover={isActive ? { bg: 'hsp.600' } : { bg: 'hsp.100', boxShadow: '0 0 0 3px var(--chakra-colors-hsp-200)' }}
        as={Button}
        bg={isActive ? 'hsp.500' : 'transparent'}
        borderColor="transparent"
        borderRadius="md"
        borderWidth="1px"
        color={isActive ? 'white' : 'hsp.500'}
        onMouseEnter={onOpen}
        onMouseLeave={onClose}
        p={2}
        rightIcon={<ChevronDown className="ms-1" />}
        rounded="lg">
        {label}
      </MenuButton>
      <Portal>
        <MenuList
          borderColor="hsp.500"
          onMouseEnter={onOpen}
          onMouseLeave={onClose}>
          <MenuOptionGroup title="HSP">
            <RadioMenuItem
              icon={
                <CheckDouble
                  size="lg"
                  widthAuto
                />
              }
              isSelected={selectedMenu === 'internal-all'}
              replace
              state={{ selectedMenu: 'internal-all' }}
              to={`/datastream/${pathSegment}`}>
              All
            </RadioMenuItem>
            <RadioMenuItem
              icon={
                <Pickaxe
                  size="lg"
                  widthAuto
                />
              }
              isSelected={selectedMenu === 'internal-prospect'}
              replace
              state={{ selectedMenu: 'internal-prospect' }}
              to={{
                pathname: `/datastream/${pathSegment}`,
                search: `?${createSearchParams({ tags: 'MailingType:Prospect' })}`,
              }}>
              Prospect
            </RadioMenuItem>
            <RadioMenuItem
              icon={
                <HouseUser
                  size="lg"
                  widthAuto
                />
              }
              isSelected={selectedMenu === 'internal-house'}
              replace
              state={{ selectedMenu: 'internal-house' }}
              to={{
                pathname: `/datastream/${pathSegment}`,
                search: `?${createSearchParams({ tags: 'MailingType:House' })}`,
              }}>
              House
            </RadioMenuItem>
            {hspMenuItemsSlot?.({ pathSegment, selectedMenu })}
          </MenuOptionGroup>
          <HasAccess permissions={['dts-view-mailings-external']}>
            <MenuDivider />
            <MenuOptionGroup title="External">
              <RadioMenuItem
                icon={
                  <CheckDouble
                    size="lg"
                    widthAuto
                  />
                }
                isSelected={selectedMenu === 'external-all'}
                replace
                state={{ selectedMenu: 'external-all' }}
                to={{
                  pathname: `/datastream/${pathSegment}/external`,
                }}>
                All
              </RadioMenuItem>
              <RadioMenuItem
                icon={
                  <Pickaxe
                    size="lg"
                    widthAuto
                  />
                }
                isSelected={selectedMenu === 'external-prospect'}
                replace
                state={{ selectedMenu: 'external-prospect' }}
                to={{
                  pathname: `/datastream/${pathSegment}/external`,
                  search: `?${createSearchParams({ tags: 'MailingType:External Prospect' })}`,
                }}>
                Prospect
              </RadioMenuItem>
              <RadioMenuItem
                icon={
                  <HouseUser
                    size="lg"
                    widthAuto
                  />
                }
                isSelected={selectedMenu === 'external-house'}
                replace
                state={{ selectedMenu: 'external-house' }}
                to={{
                  pathname: `/datastream/${pathSegment}/external`,
                  search: `?${createSearchParams({ tags: 'MailingType:External House' })}`,
                }}>
                House
              </RadioMenuItem>
              {externalMenuItemsSlot?.({ pathSegment, selectedMenu })}
            </MenuOptionGroup>
          </HasAccess>
          {bottomMenuItemsSlot ? (
            <>
              <MenuDivider />
              <MenuOptionGroup>{bottomMenuItemsSlot({ pathSegment, selectedMenu })}</MenuOptionGroup>
            </>
          ) : null}
          {children}
        </MenuList>
      </Portal>
    </Menu>
  );
};

export default MailingTypeMenu;
